import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { billingService } from '../services/billingService';
import { getEntitlements } from '../utils/billingEntitlements';
import logger from '../utils/logger';
import {
  normalizeBillingStatus,
  EMPTY_BILLING_STATUS,
} from '../utils/billingStatusState';
import {
  mergeSeededBillingStatus,
  resolveBillingRefreshFailureState,
  resolveInitialBillingProviderState,
  shouldHydrateBillingProvider,
} from '../utils/billingProviderState';

const BillingContext = createContext(null);

const billingStatusesEqual = (left, right) => {
  const normalizedLeft = normalizeBillingStatus(left);
  const normalizedRight = normalizeBillingStatus(right);
  const keys = new Set([
    ...Object.keys(normalizedLeft),
    ...Object.keys(normalizedRight),
  ]);

  for (const key of keys) {
    if (normalizedLeft[key] !== normalizedRight[key]) {
      return false;
    }
  }

  return true;
};

export const BillingProvider = ({ children, authenticatedFetch, onboardingComplete, initialBillingStatus }) => {
  const [billingStatus, setBillingStatus] = useState(() =>
    resolveInitialBillingProviderState(initialBillingStatus).billingStatus
  );
  const [paymentSuccessStatus, setPaymentSuccessStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(() =>
    resolveInitialBillingProviderState(initialBillingStatus).initialized
  );
  const billingStatusRef = useRef(billingStatus);

  useEffect(() => {
    billingStatusRef.current = billingStatus;
  }, [billingStatus]);

  const refresh = useCallback(async () => {
    if (!authenticatedFetch || !onboardingComplete) {
      return billingStatusRef.current;
    }
    setLoading(true);
    try {
      const data = await billingService.getStatus(authenticatedFetch);
      const normalized = normalizeBillingStatus(data);
      setBillingStatus((current) => (
        billingStatusesEqual(current, normalized) ? current : normalized
      ));
      setInitialized(true);
      return normalized;
    } catch (err) {
      logger.error('BillingContext: Failed to refresh billing status', err);
      const failureState = resolveBillingRefreshFailureState({
        currentBillingStatus: billingStatusRef.current,
      });
      setBillingStatus((current) => (
        billingStatusesEqual(current, failureState.billingStatus)
          ? current
          : failureState.billingStatus
      ));
      setInitialized(true);
      return failureState.billingStatus;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, onboardingComplete]);

  // Hydrate once after onboarding completes
  useEffect(() => {
    if (!shouldHydrateBillingProvider({ onboardingComplete, initialized })) return;
    refresh();
  }, [onboardingComplete, initialized, refresh]);

  // Seed from initial /auth/me response to avoid a round trip
  const seedFromAuthMe = useCallback((billingFromAuthMe) => {
    if (!billingFromAuthMe) return;
    const seededState = mergeSeededBillingStatus({
      previousBillingStatus: billingStatusRef.current,
      billingFromAuthMe,
    });
    setBillingStatus((current) => (
      billingStatusesEqual(current, seededState.billingStatus)
        ? current
        : seededState.billingStatus
    ));
    setInitialized((prev) => prev || seededState.initialized);
  }, []);

  const showPaymentSuccess = useCallback((status) => {
    setPaymentSuccessStatus(normalizeBillingStatus(status));
  }, []);

  const clearPaymentSuccess = useCallback(() => {
    setPaymentSuccessStatus(null);
  }, []);

  const entitlements = getEntitlements(billingStatus.accessState);

  const value = {
    billingStatus,
    paymentSuccessStatus,
    entitlements,
    loading,
    initialized,
    refresh,
    seedFromAuthMe,
    showPaymentSuccess,
    clearPaymentSuccess,
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};

const SAFE_FALLBACK = {
  billingStatus: EMPTY_BILLING_STATUS,
  paymentSuccessStatus: null,
  entitlements: getEntitlements('not_provisioned'),
  loading: false,
  initialized: false,
  refresh: () => {},
  seedFromAuthMe: () => {},
  showPaymentSuccess: () => {},
  clearPaymentSuccess: () => {},
};

export const useBilling = () => {
  const ctx = useContext(BillingContext);
  return ctx ?? SAFE_FALLBACK;
};

export default BillingContext;
